import { useEffect } from 'react';
import { Outlet, useLocation } from 'react-router-dom';

import useMediaQuery from '@mui/material/useMediaQuery';
import Container from '@mui/material/Container';
import Toolbar from '@mui/material/Toolbar';
import Box from '@mui/material/Box';

// project imports
import Drawer from './Drawer';
import Header from './Header';
import Footer from './Footer';
import HorizontalBar from './Drawer/HorizontalBar';
import Loader from '../../components/Loader';
import Breadcrumbs from '../../components/@extended/Breadcrumbs';
import { PageActionProvider } from '../../contexts/PageActionContext';
import { PageNotificationProvider } from '../../contexts/PageNotificationContext';

import { MenuOrientation } from '../../config';
import useConfig from '../../hooks/useConfig';
import { handlerDrawerOpen, useGetMenuMaster } from '../../api/menu';

// ==============================|| MAIN LAYOUT ||============================== //

export default function DashboardLayout() {
  const { pathname } = useLocation();
  const { menuMasterLoading } = useGetMenuMaster();
  const downXL = useMediaQuery((theme) => theme.breakpoints.down('xl'));
  const downLG = useMediaQuery((theme) => theme.breakpoints.down('lg'));

  const { state } = useConfig();
  const { container, miniDrawer, menuOrientation } = state;

  const isHorizontal = menuOrientation === MenuOrientation.HORIZONTAL && !downLG;

  // set media wise responsive drawer
  useEffect(() => {
    if (!miniDrawer) {
      handlerDrawerOpen(!downXL);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [downXL]);

  // Temporarily bypass menu loading to debug dashboard issue
  // if (menuMasterLoading) return <Loader />;

  return (
    <PageActionProvider>
      <PageNotificationProvider>
      <Box sx={{ display: 'flex', width: '100%' }}>
        <Header />
        {!isHorizontal ? <Drawer /> : <HorizontalBar />}

        <Box component="main" sx={{ width: 'calc(100% - 240px)', flexGrow: 1, p: { xs: 2, sm: 3 } }}>
          <Toolbar sx={{ mt: isHorizontal ? 8 : 'inherit' }} />
          <Container
            maxWidth={container ? 'xl' : false}
            sx={{
              ...(container && { px: { xs: 0, sm: 2 } }),
              position: 'relative',
              minHeight: 'calc(100vh - 110px)',
              display: 'flex',
              flexDirection: 'column'
            }}
          >
            {pathname !== '/apps/profiles/account/my-account' &&
              !pathname.includes('/users/permissions') &&
              !pathname.includes('/clients') &&
              !pathname.includes('/divisions') &&
              !pathname.includes('/users') &&
              pathname !== '/profile' &&
              !pathname.includes('/centres') &&
              !pathname.includes('/dentists') &&
              !pathname.includes('/specialists') &&
              !pathname.includes('/emergency-dentists') &&
              !pathname.includes('/group-plans') &&
              !pathname.includes('/fee-guides') &&
              !pathname.includes('/members') &&
              !pathname.includes('/dependents') &&
              pathname !== '/audit' &&
              pathname !== '/emails-sent' &&
              pathname !== '/system-info' &&
              !pathname.startsWith('/reports') &&
              <Breadcrumbs />}

            {pathname === '/users' &&
              <Breadcrumbs
                custom
                heading="Users"
                links={[
                  { title: 'Home', to: '/' },
                  { title: 'Users' }
                ]}
              />}

            {pathname.includes('/users/permissions') &&
              <Breadcrumbs
                custom
                heading="User Permissions"
                links={[
                  { title: 'Home', to: '/' },
                  { title: 'Users', to: '/users' },
                  { title: 'Permissions' }
                ]}
              />}

            {pathname === '/profile' &&
              <Breadcrumbs
                custom
                heading="My Profile"
                links={[
                  { title: 'Home', to: '/' },
                  { title: 'Profile' }
                ]}
              />}

            {pathname === '/system-info' &&
              <Breadcrumbs
                custom
                heading="System Info"
                links={[
                  { title: 'Home', to: '/' },
                  { title: 'System Info' }
                ]}
              />}

            {pathname === '/clients' &&
              <Breadcrumbs
                custom
                heading="Clients"
                links={[
                  { title: 'Home', to: '/' },
                  { title: 'Clients' }
                ]}
              />}

            {pathname === '/divisions' &&
              <Breadcrumbs
                custom
                heading="Divisions"
                links={[
                  { title: 'Home', to: '/' },
                  { title: 'Clients', to: '/clients' },
                  { title: 'Divisions' }
                ]}
              />}

            {pathname === '/centres' &&
              <Breadcrumbs
                custom
                heading="Centres"
                links={[
                  { title: 'Home', to: '/' },
                  { title: 'Centres' }
                ]}
              />}

            {pathname === '/centres/splits' &&
              <Breadcrumbs
                custom
                heading="GPSP Splits"
                links={[
                  { title: 'Home', to: '/' },
                  { title: 'Centres', to: '/centres' },
                  { title: 'GPSP Splits' }
                ]}
              />}

            {pathname === '/dentists' &&
              <Breadcrumbs
                custom
                heading="Dentists"
                links={[
                  { title: 'Home', to: '/' },
                  { title: 'Centres', to: '/centres' },
                  { title: 'Dentists' }
                ]}
              />}

            {pathname === '/specialists' &&
              <Breadcrumbs
                custom
                heading="Specialists"
                links={[
                  { title: 'Home', to: '/' },
                  { title: 'Specialists' }
                ]}
              />}

            {pathname === '/emergency-dentists' &&
              <Breadcrumbs
                custom
                heading="Emergency Dentists"
                links={[
                  { title: 'Home', to: '/' },
                  { title: 'Emergency Dentists' }
                ]}
              />}

            {pathname === '/group-plans/expiring' &&
              <Breadcrumbs
                custom
                heading="Expiring"
                links={[
                  { title: 'Home', to: '/' },
                  { title: 'Group Plans', to: '/group-plans' },
                  { title: 'Expiring' }
                ]}
              />}

            {pathname === '/fee-guides' &&
              <Breadcrumbs
                custom
                heading="Fee Guides"
                links={[
                  { title: 'Home', to: '/' },
                  { title: 'Fee Guides' }
                ]}
              />}

            {pathname === '/members/centre-transfer' &&
              <Breadcrumbs
                custom
                heading="Centre Transfer"
                links={[
                  { title: 'Home', to: '/' },
                  { title: 'Members', to: '/members' },
                  { title: 'Centre Transfer' }
                ]}
              />}

            {pathname === '/members' &&
              <Breadcrumbs
                custom
                heading="Members"
                links={[
                  { title: 'Home', to: '/' },
                  { title: 'Members' }
                ]}
              />}

            {pathname === '/dependents' &&
              <Breadcrumbs
                custom
                heading="Dependents"
                links={[
                  { title: 'Home', to: '/' },
                  { title: 'Members', to: '/members' },
                  { title: 'Dependents' }
                ]}
              />}

            {pathname === '/group-plans' &&
              <Breadcrumbs
                custom
                heading="Group Plans"
                links={[
                  { title: 'Home', to: '/' },
                  { title: 'Group Plans' }
                ]}
              />}

            {pathname.includes('/group-plans/coverage') &&
              <Breadcrumbs
                custom
                heading="Coverage"
                links={[
                  { title: 'Home', to: '/' },
                  { title: 'Group Plans', to: '/group-plans' },
                  { title: 'Coverage' }
                ]}
              />}

            {pathname.includes('/group-plans/excluded-procedures') &&
              <Breadcrumbs
                custom
                heading="Excluded Procedures"
                links={[
                  { title: 'Home', to: '/' },
                  { title: 'Group Plans', to: '/group-plans' },
                  { title: 'Excluded Procedures' }
                ]}
              />}

            {pathname.includes('/group-plans/benefit-years/') &&
              <Breadcrumbs
                custom
                heading="Benefit Years"
                links={[
                  { title: 'Home', to: '/' },
                  { title: 'Group Plans', to: '/group-plans' },
                  { title: 'Benefit Years' }
                ]}
              />}

            {pathname === '/reports/members' &&
              <Breadcrumbs
                custom
                heading="Member Reports"
                links={[
                  { title: 'Home', to: '/' },
                  { title: 'Reports', to: '/reports' },
                  { title: 'Member Reports' }
                ]}
              />}

            {pathname === '/reports/invoices' &&
              <Breadcrumbs
                custom
                heading="Invoice Reports"
                links={[
                  { title: 'Home', to: '/' },
                  { title: 'Reports', to: '/reports' },
                  { title: 'Invoice Reports' }
                ]}
              />}

            {pathname === '/reports/claims' &&
              <Breadcrumbs
                custom
                heading="Claim Reports"
                links={[
                  { title: 'Home', to: '/' },
                  { title: 'Reports', to: '/reports' },
                  { title: 'Claim Reports' }
                ]}
              />}

            {pathname === '/reports/browser' &&
              <Breadcrumbs
                custom
                heading="Report Browser"
                links={[
                  { title: 'Home', to: '/' },
                  { title: 'Reports', to: '/reports' },
                  { title: 'Report Browser' }
                ]}
              />}

            {pathname === '/reports/eligibility' &&
              <Breadcrumbs
                custom
                heading="Eligibility"
                links={[
                  { title: 'Home', to: '/' },
                  { title: 'Reports', to: '/reports' },
                  { title: 'Eligibility' }
                ]}
              />}

            {pathname === '/reports/broker' &&
              <Breadcrumbs
                custom
                heading="Broker"
                links={[
                  { title: 'Home', to: '/' },
                  { title: 'Reports', to: '/reports' },
                  { title: 'Broker' }
                ]}
              />}

            {pathname === '/reports' &&
              <Breadcrumbs
                custom
                heading="Reports"
                links={[
                  { title: 'Home', to: '/' },
                  { title: 'Reports' }
                ]}
              />}

            {pathname === '/audit' &&
              <Breadcrumbs
                custom
                heading="Audit"
                links={[
                  { title: 'Home', to: '/' },
                  { title: 'Audit' }
                ]}
              />}

            {pathname === '/emails-sent' &&
              <Breadcrumbs
                custom
                heading="Emails Sent"
                links={[
                  { title: 'Home', to: '/' },
                  { title: 'Emails Sent' }
                ]}
              />}

            <Outlet />
            {/*
            <Footer />
            */}
          </Container>
        </Box>
      </Box>
      </PageNotificationProvider>
    </PageActionProvider>
  );
}